/* eslint-disable react-hooks/exhaustive-deps */
import {useQuery} from "@tanstack/react-query"
import {useEffect} from "react"
import {get, head, map} from "lodash"
import {courseService, taskService} from "services"
import {KlassappTable, KlassappTableHeader, AssignTaskPopup} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {BaseButton, Icon} from "components"
import {KlassappTableProps} from "types/common"
import {handleError} from "helpers"
import styles from "./StudentCustomTasks.module.css"
import {useHistory} from "react-router-dom"
import {useVisible} from "hooks"

type Props = KlassappTableProps & {
    termId: number
    courseId: number
    scheduleId: number
    studentProfileIds: number[]
}

function StudentCustomTasks(props: Props) {
    const {
        dispatch,
        dispatchFunc,
        page,
        pageSize,
        columns,
        allFields,
        fields,
        isLoading,
        termId,
        courseId,
        scheduleId,
        studentProfileIds
    } = props
    const history = useHistory()
    const assignTaskPopup = useVisible(false)
    const {data: courseSettings} = useQuery(
        ["course-detail", courseId],
        () =>
            courseService.getOne(courseId, {
                linkedEntities: ["tasks"]
            }),
        {onError: handleError}
    )

    const {
        data: {data: customTasks, total: totalCustomTasks},
        isFetching: isLoadingCustomTasks,
        refetch
    } = useQuery(
        ["student-custom-tasks", studentProfileIds, courseId, termId],
        async () => {
            const result = await taskService.getCustomDigitalTasksOfStudent({
                filter: {
                    profileIds: studentProfileIds,
                    courseIds: [courseId],
                    termIds: termId ? [termId] : undefined,
                    scheduleIds: scheduleId ? [scheduleId] : undefined
                }
            })
            const tasks = get(head(result), "tasks", []).map((task) => ({
                ...task,
                approveHtml: task.isApproved && (
                    <Icon icon="CHECKMARK_CIRCLE" className={styles.approveIcon} color="#18A957" />
                )
            }))
            return {data: tasks, total: tasks.length}
        },
        {onError: (err) => handleError(err), initialData: {data: [], total: 0}}
    )

    useEffect(() => {
        dispatch({isLoading: isLoadingCustomTasks})
    }, [isLoadingCustomTasks])

    const getFields = () => {
        return ["Task Id", "Task Name", "Approve"]
    }

    const getPageTitle = () => {
        return "Custom Tasks"
    }

    const getColumns = () => {
        return [
            {
                title: "Task Id",
                field: "taskId",
                style: {minWidth: "100px"}
            },
            {
                title: "Task Name",
                field: "name",
                style: {minWidth: "100px"}
            },
            {
                title: "Approve",
                field: "approveHtml",
                style: {minWidth: "100px"}
            }
        ]
    }

    const onClickRowItem = (task) => {
        history.push(`/tasks/submissions?id=${task.taskId}&type=customDD`)
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "onClickRowItem", func: onClickRowItem}
        ])
    }, [])

    return (
        <div>
            <div className={styles.action}>
                <BaseButton title="Create Task" onClick={assignTaskPopup.open} />
            </div>
            <KlassappTableHeader
                actions={[]}
                page={page}
                total={totalCustomTasks}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={customTasks}
                menuActions={[]}
                isLoading={isLoading}
                allFields={allFields}
                onClickRowItem={onClickRowItem}
            />
            <AssignTaskPopup
                isShow={assignTaskPopup.isVisible}
                onClose={assignTaskPopup.close}
                studentProfileId={head(studentProfileIds)}
                termId={termId}
                courseId={courseId}
                scheduleId={scheduleId}
                edularTasks={courseSettings?.edularTasks || []}
            />
        </div>
    )
}

export default KlassappTableHOC(StudentCustomTasks)
